"use client";

import {
  X,
  Brain,
  Calculator,
  Gamepad2,
  BookOpen,
  Star,
  Sparkles,
  PlayCircle,
  Globe,
  Biohazard,
  Flag,
  File,
  FlaskConical,
  BookOpenCheck,
  Diff,
  Landmark,
  Drill,
  TrendingUp,
  Shield,
  Briefcase,
  Search,
  Zap,
  Scale,
  Currency,
  Coins,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";

interface GameSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subjectTitle: string;
  subjectId: string;
}

export default function GameSelectionModal({
  isOpen,
  onClose,
  subjectTitle,
  subjectId,
}: GameSelectionModalProps) {
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Master Game List with Subject mapping
  const gamesList = [
    // Economics
    {
      id: "eco-1",
      sId: "economics",
      title: "Budget Blitz",
      icon: Calculator,
      gradient: "from-purple-400 to-indigo-500",
      route: "/budget-blitz",
    },
    {
      id: "eco-2",
      sId: "economics",
      title: "Naira Tycoon",
      icon: Coins,
      gradient: "from-green-400 to-teal-600",
      route: "/naira-tycoon",
    },

    // Commerce
    {
      id: "com-1",
      sId: "commerce",
      title: "Naira Tycoon",
      icon: Landmark,
      gradient: "from-emerald-400 to-teal-500",
      route: "/naira-tycoon",
    },
    {
      id: "com-2",
      sId: "commerce",
      title: "Capital Scramble",
      icon: Globe,
      gradient: "from-blue-400 to-cyan-500",
      route: "/capital-scramble",
    },

    // English
    {
      id: "eng-1",
      sId: "english",
      title: "Word Smith",
      icon: BookOpenCheck,
      gradient: "from-purple-400 to-indigo-500",
      route: "/wordsmith",
    },
    {
      id: "eng-2",
      sId: "english",
      title: "Cards Matching",
      icon: File,
      gradient: "from-pink-400 to-rose-500",
      route: "/match-game",
    },

    // Government / Civics
    {
      id: "gov-1",
      sId: "government",
      title: "Diplomats Desk",
      icon: Flag,
      gradient: "from-blue-400 to-cyan-500",
      route: "/diplomats-desk",
    },
    {
      id: "gov-2",
      sId: "government",
      title: "Civic Spin",
      icon: Scale,
      gradient: "from-indigo-400 to-purple-500",
      route: "/civic-spin",
    },

    // Chemistry
    {
      id: "che-1",
      sId: "chemistry",
      title: "Compound Connector",
      icon: FlaskConical,
      gradient: "from-red-400 to-red-600",
      route: "/compound-connector",
    },

    // Biology
    {
      id: "bio-1",
      sId: "biology",
      title: "Biology Kingdom",
      icon: Biohazard,
      gradient: "from-green-400 to-teal-600",
      route: "/biology-kingdom",
    },

    // Mathematics
    {
      id: "mat-1",
      sId: "mathematics",
      title: "Equation Scape",
      icon: Diff,
      gradient: "from-blue-400 to-blue-600",
      route: "/equation-scape",
    },

    // Business Studies / Trade
    {
      id: "bus-1",
      sId: "business-studies",
      title: "Budget Blitz",
      icon: Calculator,
      gradient: "from-yellow-400 to-yellow-600",
      route: "/budget-blitz",
    },

    {
      id: "bus-2",
      sId: "business-studies",
      title: "Naira Tycoon",
      icon: Landmark,
      gradient: "from-slate-400 to-slate-600",
      route: "/naira-tycoon",
    },

    // Financial Accounting
    // {
    //   id: "acc-1",
    //   sId: "financial-accounting",
    //   title: "Ledger Legend",
    //   icon: Calculator,
    //   gradient: "from-purple-400 to-purple-600",
    //   route: "/ledger-legend",
    // },
    // {
    //   id: "acc-2",
    //   sId: "financial-accounting",
    //   title: "Balance Bot",
    //   icon: Gamepad2,
    //   gradient: "from-indigo-400 to-indigo-600",
    //   route: "/balance-bot",
    // },

    // Marketing
    {
      id: "mkt-1",
      sId: "marketing",
      title: "Brand Builder",
      icon: Star,
      gradient: "from-orange-400 to-orange-600",
      route: "/brand-builder",
    },
    {
      id: "mkt-2",
      sId: "marketing",
      title: "Ad Venture",
      icon: PlayCircle,
      gradient: "from-red-400 to-red-600",
      route: "/ad-venture",
    },

    // General / Insurance / Data Processing
    // {
    //   id: "gen-1",
    //   sId: "insurance",
    //   title: "Risk Reward",
    //   icon: Shield,
    //   gradient: "from-blue-400 to-blue-600",
    //   route: "/risk-reward",
    // },
    // {
    //   id: "gen-2",
    //   sId: "insurance",
    //   title: "Safety First",
    //   icon: Lock,
    //   gradient: "from-teal-400 to-teal-600",
    //   route: "/safety-first",
    // },

    // Geography
    {
      id: "geo-1",
      sId: "geo-1",
      title: "Capital Scramble",
      icon: Globe,
      gradient: "from-green-400 to-green-600",
      route: "/capital-scramble",
    },

    //Literature
    {
      id: "lit-1",
      sId: "literature",
      title: "Word Smith",
      icon: BookOpen,
      gradient: "from-purple-400 to-purple-600",
      route: "/wordsmith",
    },
  ];

  // Filter games based on current subjectId
  const displayGames = gamesList.filter((game) => game.sId === subjectId);

  // Fallback: if no games are found, show related generic ones
  const finalGames =
    displayGames.length > 0 ? displayGames : gamesList.slice(0, 2);

  const handlePlayClick = (gameId: string, route: string) => {
    setSelectedGame(gameId);
    setTimeout(() => {
      navigate(route);
      setSelectedGame(null);
      onClose();
    }, 400);
  };

  return (
    <div className="fixed inset-0 bg-indigo-900/60 flex items-center justify-center z-50 p-4">
      <section
        ref={modalRef}
        className="bg-white w-full max-w-2xl max-h-[85vh] flex flex-col rounded-[1rem] shadow-2xl relative transition-transform duration-300 transform scale-100 animate-fadeIn"
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-8 p-2 bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 rounded-2xl transition-all z-20"
        >
          <X className="w-6 h-6 stroke-[3px]" />
        </button>

        <div className="flex-1 overflow-y-auto p-8 md:p-12 scrollbar-thin scrollbar-thumb-teal-600 scrollbar-track-slate-100">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 text-yellow-700 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
              <Star className="w-4 h-4 fill-yellow-500" />
              Adventure Awaits!
              <Sparkles className="w-4 h-4 fill-yellow-500" />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-indigo-950 tracking-tight leading-tight">
              Ready to <span className="text-indigo-600">Play?</span>
            </h2>
            <p className="text-slate-500 mt-3 text-lg font-medium">
              Pick a mission for{" "}
              <span className="text-indigo-600 font-bold">{subjectTitle}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-4">
            {finalGames.map((game) => {
              const Icon = game.icon;
              const isSelected = selectedGame === game.id;

              return (
                <button
                  key={game.id}
                  onClick={() => handlePlayClick(game.id, game.route)}
                  disabled={selectedGame !== null && !isSelected}
                  className={`
                    group relative flex flex-col items-center p-6 rounded-[2.5rem] border-b-8 border-r-4 transition-all duration-300
                    ${game.gradient} bg-gradient-to-br border-black/10 hover:translate-y-[-4px] active:translate-y-[2px] active:border-b-2
                    ${isSelected ? "scale-95 brightness-90" : "hover:shadow-xl"}
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <div className="bg-white/30 p-4 rounded-3xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-10 h-10 text-white" strokeWidth={2.5} />
                  </div>

                  <span className="text-xl font-black text-white mb-2 text-center">
                    {game.title}
                  </span>

                  <div className="flex items-center gap-2 bg-white/20 px-4 py-1 rounded-full text-white/90 text-sm font-bold">
                    <PlayCircle className="w-4 h-4" />
                    {isSelected ? "Opening..." : "Go!"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-6 bg-slate-50 border-t border-slate-100 flex gap-2 justify-center items-center overflow-hidden shrink-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-slate-200 shrink-0"
            />
          ))}
        </div>
      </section>
    </div>
  );
}
